import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sessionAPI } from '../services/api';
import { getRecommendationBadge, getSessionRecommendation } from '../utils/hiringRecommendation';
import { USE_DEMO_DATA } from '../data/demoMode';
import { DUMMY_SESSIONS, withCurrentRecruiterInDemoSessions } from '../data/dashboardDummyData';

const BRAND = '#6366F1';

/** Thin stroke KPI icons (match Dashboard). */
function KpiLineIcon({ children, color = '#94A3B8' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      style={{ flexShrink: 0, display: 'block' }}
      aria-hidden
    >
      {children}
    </svg>
  );
}

const Interviews = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const initialFilter =
    statusFromUrl && ['all', 'completed', 'in_progress'].includes(statusFromUrl)
      ? statusFromUrl
      : 'all';
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const loadSessions = async () => {
      try {
        setLoading(true);
        if (USE_DEMO_DATA) {
          setSessions(withCurrentRecruiterInDemoSessions(DUMMY_SESSIONS));
          return;
        }
        const response = await sessionAPI.getAllSessions();
        setSessions(response.data || []);
      } catch (error) {
        console.error('Error loading interview sessions:', error);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    loadSessions();
  }, []);

  const filtered = (sessions ?? []).filter(s =>
    filter === "all" || (s.status ?? "") === filter
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIndex = (page - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "28px 32px" }}>

      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", marginBottom: 3 }}>Interviews</div>
        <div style={{ fontSize: 12, color: "#475569" }}>All interview sessions across positions.</div>
      </div>

      {/* Stat cards — same pattern as Dashboard: top accent, grey SVG icons, sub-caption */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          {
            label: "Total Sessions",
            value: (sessions ?? []).length,
            accent: BRAND,
            sub: "All time",
            shadow: `${BRAND}15`,
            icon: (
              <KpiLineIcon>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </KpiLineIcon>
            ),
          },
          {
            label: "Completed",
            value: (sessions ?? []).filter((s) => (s.status ?? "") === "completed").length,
            accent: "#10B981",
            sub: "Finished sessions",
            shadow: "#10B98115",
            icon: (
              <KpiLineIcon>
                <polyline points="20 6 9 17 4 12" />
              </KpiLineIcon>
            ),
          },
          {
            label: "In Progress",
            value: (sessions ?? []).filter((s) => (s.status ?? "") === "in_progress").length,
            accent: "#F59E0B",
            sub: "Active now",
            shadow: "#F59E0B15",
            icon: (
              <KpiLineIcon>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </KpiLineIcon>
            ),
          },
          {
            label: "Recommended",
            value: (sessions ?? []).filter((s) => {
              const { key } = getSessionRecommendation(s);
              return key === "strong_yes" || key === "yes";
            }).length,
            accent: BRAND,
            sub: "Hire signals",
            shadow: `${BRAND}15`,
            icon: (
              <KpiLineIcon>
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </KpiLineIcon>
            ),
          },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: "#FFFFFF",
              borderRadius: 12,
              padding: "18px 20px",
              border: "1px solid #E2E8F0",
              position: "relative",
              overflow: "hidden",
              transition: "all .2s",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 4px 14px ${stat.shadow}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: stat.accent,
                borderRadius: "10px 10px 0 0",
                opacity: 0.9,
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 9,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                  fontWeight: 700,
                }}
              >
                {stat.label}
              </div>
              {stat.icon}
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#0F172A", lineHeight: 1, marginBottom: 4 }}>{stat.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: stat.accent }}>{stat.sub}</div>
        </div>
        ))}
        </div>

      {/* Filter pills */}
      <div
        style={{
          display: "flex",
          gap: 2,
          marginBottom: 20,
          background: "#F3F4F6",
          padding: 3,
          borderRadius: 8,
          border: "1px solid #E5E7EB",
          width: "fit-content",
        }}
      >
        {[
          { value: "all", label: "All" },
          { value: "completed", label: "Completed" },
          { value: "in_progress", label: "In Progress" },
        ].map(f => (
          <button
            type="button"
            key={f.value}
            onClick={() => setFilter(f.value)}
            style={
              filter === f.value
                ? {
                    padding: "5px 14px",
                    borderRadius: 6,
                    border: "none",
                    background: "#FFFFFF",
                    color: "#111827",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .14s",
                    boxShadow: "0 1px 2px rgba(0,0,0,.08)",
                    whiteSpace: "nowrap",
                  }
                : {
                    padding: "5px 14px",
                    borderRadius: 6,
                    border: "none",
                    background: "transparent",
                    color: "#6B7280",
                    fontSize: 12,
                    fontWeight: 400,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .14s",
                    whiteSpace: "nowrap",
                  }
            }
          >
            {f.label}
          </button>
        ))}
        </div>

      {/* Sessions table */}
      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Candidate", "Position", "Started", "Duration", "Score", "Status", "Recommendation", ""].map(h => (
                <th key={h} style={{ padding: "9px 18px", fontSize: 9, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1.1px", fontWeight: 700, borderBottom: "1px solid #E2E8F0", background: "#F8FAFC", textAlign: "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((s, i) => {
              const name = s.candidate_name ?? s.name ?? s.candidate ?? "Unknown";
              const role = s.role ?? s.job_role ?? s.position ?? "";
              const campaign = s.campaign_name ?? s.campaign ?? "";
              const started = s.started_at ?? s.start_time ?? s.created_at ?? s.started ?? "—";
              const duration = s.duration ?? s.interview_duration ?? s.duration_seconds ?? "—";
              const score = s.overall_score ?? s.score ?? 0;
              const status = s.status ?? "pending";
              const badge = getRecommendationBadge(s);

              const scoreColor = score >= 8 ? "#10B981" : score >= 6 ? "#F59E0B" : "#EF4444";

              const statusStyle = status === "completed"
                ? { bg: "#ECFDF5", color: "#10B981", label: "Completed" }
                : status === "in_progress"
                  ? { bg: "#EEF2FF", color: BRAND, label: "In Progress" }
                  : { bg: "#F1F5F9", color: "#94A3B8", label: "Pending" };

              let startedDisplay = started;
              if (typeof started === "string" && started !== "—" && started.includes("T")) {
                const d = new Date(started);
                startedDisplay = `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
              }

              const durationDisplay = typeof duration === "number"
                ? `${Math.floor(duration / 60)}m ${duration % 60}s`
                : duration;
              const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

              return (
                <tr
                  key={s.id ?? s.session_id ?? `${startIndex + i}`}
                  style={{ transition: "background .14s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  {/* Candidate */}
                  <td style={{ padding: "12px 18px", borderBottom: "1px solid #E2E8F0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(99,102,241,.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: BRAND, flexShrink: 0 }}>
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{name}</div>
                        <div style={{ fontSize: 10, color: "#94A3B8" }}>{role}</div>
                      </div>
                    </div>
                  </td>

                  {/* Campaign */}
                  <td style={{ padding: "12px 18px", fontSize: 12, color: "#475569", borderBottom: "1px solid #E2E8F0" }}>
                    {campaign}
                  </td>

                  {/* Started */}
                  <td style={{ padding: "12px 18px", fontSize: 11, color: "#475569", borderBottom: "1px solid #E2E8F0" }}>
                    {startedDisplay}
                  </td>

                  {/* Duration */}
                  <td style={{ padding: "12px 18px", fontSize: 12, color: "#475569", borderBottom: "1px solid #E2E8F0" }}>
                    {durationDisplay}
                  </td>

                  {/* Score */}
                  <td style={{ padding: "12px 18px", fontSize: 14, fontWeight: 800, color: scoreColor, borderBottom: "1px solid #E2E8F0" }}>
                    {typeof score === "number" ? score.toFixed(1) : score}
                  </td>

                  {/* Status */}
                  <td style={{ padding: "12px 18px", borderBottom: "1px solid #E2E8F0" }}>
                    <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, fontWeight: 700 }}>
                      {statusStyle.label}
                    </span>
                  </td>

                  {/* Recommendation */}
                  <td style={{ padding: "12px 18px", borderBottom: "1px solid #E2E8F0" }}>
                    <span style={{ fontSize: 10, background: badge.bg, color: badge.color, padding: "3px 9px", borderRadius: 20, fontWeight: 700 }}>
                      {badge.label}
                    </span>
                  </td>

                  {/* Action */}
                  <td style={{ padding: "12px 18px", borderBottom: "1px solid #E2E8F0" }}>
                    <button
                      type="button"
                      style={{ fontSize: 11, color: BRAND, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                      onClick={(e) => {
                        e.stopPropagation();
                        const sessionId = s.id ?? s.session_id;
                        if (sessionId) {
                          navigate(`/admin/reports?sessionId=${sessionId}`);
                        }
                      }}
                    >
                      Report →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && (
          <div style={{ padding: "24px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
            Loading interview sessions...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: "48px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
            No interview sessions found.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC" }}>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filtered.length)} of {filtered.length}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 7,
                  border: "1px solid #E2E8F0",
                  background: page === 1 ? "#F1F5F9" : "white",
                  color: page === 1 ? "#94A3B8" : "#475569",
                  cursor: page === 1 ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).slice(
                Math.max(0, page - 3),
                Math.min(totalPages, page + 2)
              ).map(pn => (
                <button
                  type="button"
                  key={pn}
                  onClick={() => setPage(pn)}
                  style={{
                    fontSize: 11,
                    minWidth: 28,
                    height: 28,
                    borderRadius: 7,
                    border: "1px solid " + (pn === page ? BRAND : "#E2E8F0"),
                    background: pn === page ? "rgba(99,102,241,.1)" : "white",
                    color: pn === page ? BRAND : "#475569",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: pn === page ? 700 : 500,
                  }}
                >
                  {pn}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 7,
                  border: "1px solid #E2E8F0",
                  background: page === totalPages ? "#F1F5F9" : "white",
                  color: page === totalPages ? "#94A3B8" : "#475569",
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
    </div>
  );
};

export default Interviews;
